import React, { useImperativeHandle, useRef, useState } from 'react';
import classNames from 'classnames';
import { useControlled } from '../../hooks/useControlled';
import { useRover } from '../../hooks/useRover';
import { Radio } from './Radio';
import { getRadioGroupStyleByProps } from './styles';
import { getKeyFromItem } from '../../utils/shared';
import { AriaFocusProps } from '../../types/common';
import { DefaultRadioSizeUnion, BoxRadioSizeUnion } from './types';

export type AlignType = 'horizontal' | 'vertical';
export type RadioGroupVariant = 'default' | 'box';
export type RadioWidthType = 'fill' | 'auto';
export interface RadioVariantProps<T extends RadioGroupVariant> {
  variant?: T;
  /**
   * variant - default일 경우 'small', 'medium', 'large'만 적용되며 default값은 'medium'입니다.
   *
   * variant - box일 경우 'xsmall', 'medium', 'large'만 적용되며 default값은 'medium'입니다.
   */
  size?: T extends 'box' ? BoxRadioSizeUnion : DefaultRadioSizeUnion;
}

export interface RadioItem<RadioValue> {
  label: React.ReactNode;
  value: RadioValue;
  disabled?: boolean;
}

export interface RadioGroupProps<RadioValue>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'defaultValue' | 'value' | 'onChange'>,
    AriaFocusProps,
    RadioVariantProps<RadioGroupVariant> {
  className?: string;
  defaultValue?: RadioValue;
  value?: RadioValue;
  name?: string;
  items: RadioItem<RadioValue>[];
  /**
   * variant - box 일경우 spacing 속성은 미적용됩니다.
   * @default 24
   */
  spacing?: number;
  /** @default false */
  disabled?: boolean;
  /**
   * variant - box 일경우 align 속성은 'horizontal' 고정입니다.
   * @default 'horizontal'
   */
  align?: AlignType;
  /**
   * 'fill' 일경우 Full Width 값, 'auto' 일경우 기본 너비 값으로 적용됩니다.
   * @default 'auto'
   */
  width?: RadioWidthType;
  keyExtractor?: (item: RadioItem<RadioValue>, index: number) => string | number;
  onChange?: (value: RadioValue) => void;
}

export interface RadioGroupInstance<RadioValue> {
  value?: RadioValue;
  focus: () => void;
}

export const RadioGroup = React.forwardRef(
  <RadioValue,>(
    {
      className,
      name,
      size = 'medium',
      items,
      defaultValue,
      value,
      spacing = 24,
      disabled = false,
      align = 'horizontal',
      variant = 'default',
      width = 'auto',
      unstableFocusVisible,
      keyExtractor = getKeyFromItem,
      onChange,
      ...otherProps
    }: RadioGroupProps<RadioValue>,
    ref: React.Ref<RadioGroupInstance<RadioValue>>,
  ) => {
    const [focused_index, setFocusedIndex] = useState(0);
    const [selected_value, setSelectedValue] = useControlled({ defaultValue, value });
    const first_ref = useRef<HTMLInputElement | null>(null);

    const handleChange = (item_value: RadioValue) => {
      setSelectedValue(item_value);
      onChange?.(item_value);
    };

    const { getRoverItemProps } = useRover({
      index: focused_index,
      onIndexChange: (index, is_selecting) => {
        setFocusedIndex(index);
        if (is_selecting) {
          handleChange(items[index].value);
        }
      },
      length: items.length,
    });

    useImperativeHandle(
      ref,
      () => ({
        value: selected_value,
        focus: () => first_ref.current?.focus?.(),
      }),
      [selected_value],
    );

    return (
      <div
        className={classNames('pds-radio-group', className)}
        css={getRadioGroupStyleByProps({ variant, align, spacing, size })}
        role='group'
        {...otherProps}
      >
        {items.map((item, index) => (
          <Radio
            key={keyExtractor(item, index)}
            variant={variant}
            disabled={disabled || item.disabled}
            width={width}
            checked={selected_value === item.value}
            onChange={() => handleChange(item.value)}
            size={size}
            name={name}
            unstableFocusVisible={unstableFocusVisible}
            {...getRoverItemProps(index, {
              ref: index === 0 ? first_ref : undefined,
            })}
          >
            {item.label}
          </Radio>
        ))}
      </div>
    );
  },
) as <RadioValue>(
  props: RadioGroupProps<RadioValue> & { ref?: React.Ref<RadioGroupInstance<RadioValue>> },
) => React.ReactElement;
